/**
 * Read a KUI design token (CSS custom property) as a literal colour/value.
 *
 * Needed where a token must reach a context that can't use Tailwind classes or
 * `var(...)` — e.g. a `<canvas>` (chart.js) fill. Resolves from the live cascade
 * so it tracks the token (and any theme override); falls back on the server.
 */
export const cssToken = (name: string, fallback = ''): string => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

/**
 * Read a colour token as a hex string, for a library that cannot parse the DS's `oklch()` values
 * (chart.js derives hover colours from the string; a map pin SDK wants plain hex). The browser
 * converts by painting one canvas pixel; without a canvas the raw value or the fallback is returned.
 */
export const cssColor = (name: string, fallback = ''): string => {
  const value = cssToken(name);
  const context = value ? document.createElement('canvas').getContext('2d', { willReadFrequently: true }) : null;
  if (!context) {
    return value || fallback;
  }
  context.fillStyle = value;
  context.fillRect(0, 0, 1, 1);
  const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;

  return `#${[red, green, blue].map(channel => channel!.toString(16).padStart(2, '0')).join('')}`;
};
